import type {
  ProductItemAttributesInterface,
  ProductItemAttributesValue,
} from '../product/model/ProductItemAttributesInterface';
import type { ProductAttrComputer } from '../product/helper/ProductAttrComputer';

export class ProductCherryPickHelper {
  public lostAttributes: ProductItemAttributesInterface = {};

  public constructor(
    protected attrComputer: ProductAttrComputer,
    public attributes: ProductItemAttributesInterface = {},
  ) {}

  public reset(): void {
    this.attributes = {};
    this.lostAttributes = {};
  }

  public hasLostAttributeValue(
    attributeName: string,
    value: string | number | string[] | number[] | null | boolean,
  ): boolean {
    if (this.hasLostAttribute(attributeName)) {
      if (this.attrComputer.isMultiValue(attributeName)) {
        let hasLost: boolean = false;
        const lostValues = this.lostAttributes[attributeName] as Array<unknown>;
        if (!Array.isArray(value)) {
          // eslint-disable-next-line  @typescript-eslint/no-explicit-any
          value = [value] as Array<any>;
        }

        (value as Array<unknown>).forEach((checkValue) => {
          if (!hasLost) {
            hasLost = lostValues.indexOf(checkValue) >= 0;
          }
        });

        return hasLost;
      } else {
        return this.lostAttributes[attributeName] != value;
      }
    }

    return false;
  }

  public hasLostAttribute(attributeName: string) {
    return Object.prototype.hasOwnProperty.call(this.lostAttributes, attributeName);
  }

  public maybeRegisterALost(
    attributeName: string,
    newValues: string | number | string[] | number[] | null | boolean,
  ): boolean {
    if (this.hasAttr(attributeName)) {
      if (this.attrComputer.isMultiValue(attributeName)) {
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        const lostValues: Array<any> = [];
        const cherryPickedValue: Array<unknown> = this.getAttrValue(
          attributeName,
        ) as Array<unknown>;
        if (!Array.isArray(newValues)) {
          // eslint-disable-next-line  @typescript-eslint/no-explicit-any
          newValues = [newValues] as Array<any>;
        }
        cherryPickedValue?.forEach((value) => {
          if (value !== null && value !== undefined) {
            if ((newValues as Array<unknown>).indexOf(value) < 0) {
              lostValues.push(value);
            }
          }
        });
        if (lostValues.length) {
          this.lostAttributes[attributeName] = lostValues;

          return true;
        } else {
          delete this.lostAttributes[attributeName];
        }
      } else {
        // TODO: Doesn't work for features (multi value)
        const cherryPickedValue = this.getAttrValue(attributeName);
        const cherryPickedValueAsString = JSON.stringify(cherryPickedValue);
        const newValueAsString = JSON.stringify(newValues);
        if (cherryPickedValueAsString != newValueAsString) {
          this.lostAttributes[attributeName] = cherryPickedValue;
          return true;
        } else if (Object.prototype.hasOwnProperty.call(this.lostAttributes, attributeName)) {
          delete this.lostAttributes[attributeName];
        }
      }
    }

    return false;
  }

  public removeAttr(attributeName: string, attributeValue: string | number): void {
    if (this.attrComputer.isMultiValue(attributeName) && this.attributes[attributeName]) {
      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      const preferred = this.attributes[attributeName] as Array<any>;
      this.attributes[attributeName] = preferred?.filter((value) => value != attributeValue);
    } else {
      if (Object.prototype.hasOwnProperty.call(this.attributes, attributeName)) {
        delete this.attributes[attributeName];
      }
    }
  }

  public hasAttr(attributeName: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.attributes, attributeName);
  }

  public setAttrValue(attrName: string, attrValue: string | number | null): void {
    // TODO: Make sure it keeps track of multi values
    // Example, when selecting both backpaper printing and effect layer it seems to just remembed the last picked one?
    if (this.attrComputer.isMultiValue(attrName)) {
      if (!this.attributes[attrName]) {
        this.attributes[attrName] = [];
      }
      if (attrValue) {
        (this.attributes[attrName] as Array<string | number>).push(attrValue);
      }
    } else {
      this.attributes[attrName] = attrValue;
    }
  }

  public getAttrValue(attrName: string): ProductItemAttributesValue {
    if (this.hasAttr(attrName)) {
      return this.attributes[attrName];
    }

    return null;
  }
}
